import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, CalendarClock, Play, ThumbsUp, MessageSquare, Loader2, Wrench, List, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { WorkshopWeekCalendar } from '../components/leasing/WorkshopWeekCalendar';
import { AppointmentCard } from '../components/workshop/AppointmentCard';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { MaintenanceFormDialog } from '../components/leasing/MaintenanceFormDialog';
import { MaintStatusBadge, MAINT_STATUS, fmtDay, chatLink } from '../components/leasing/maintenanceShared';

const FILTERS = ['active', 'pending_workshop', 'date_proposed', 'scheduled', 'in_progress', 'awaiting_approval', 'completed'];
const ACTIVE = ['pending_workshop', 'date_proposed', 'scheduled', 'in_progress', 'awaiting_approval'];

const dialogs = (el) => ({
    propose: { title: el ? 'Πρόταση νέας ημερομηνίας' : 'Propose new date', fields: ['proposed_date', 'notes'], submit: el ? 'Αποστολή πρότασης' : 'Send proposal',
        endpoint: (id) => `/leasing/workshop-jobs/${id}/propose`, body: (f) => ({ proposed_date: f.proposed_date, notes: f.notes || '' }) },
    complete: { title: el ? 'Ολοκλήρωση εργασίας' : 'Complete job', fields: ['completed_date', 'mileage', 'notes'], submit: el ? 'Ολοκλήρωση' : 'Complete',
        endpoint: (id) => `/leasing/workshop-jobs/${id}/complete`, body: (f) => ({ completed_date: f.completed_date, cost: f.cost ? Number(f.cost) : null, mileage: f.mileage ? Number(f.mileage) : null, notes: f.notes || '' }) },
});

const JobActions = ({ j, el, open, quick }) => {
    const navigate = useNavigate();
    return (
        <div className="flex flex-wrap gap-2">
            {j.status === 'pending_workshop' && <Button size="sm" onClick={() => quick(j, 'accept')} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`job-accept-${j.id}`}><ThumbsUp className="w-4 h-4 mr-1" />{el ? 'Αποδοχή ραντεβού' : 'Accept'}</Button>}
            {['pending_workshop', 'scheduled'].includes(j.status) && <Button size="sm" variant="outline" onClick={() => open('propose', j)} data-testid={`job-propose-${j.id}`}><CalendarClock className="w-4 h-4 mr-1" />{el ? 'Νέα ημερομηνία' : 'Propose date'}</Button>}
            {j.status === 'scheduled' && <Button size="sm" variant="outline" onClick={() => quick(j, 'start')} className="text-sky-700" data-testid={`job-start-${j.id}`}><Play className="w-4 h-4 mr-1" />{el ? 'Έναρξη εργασίας' : 'Start job'}</Button>}
            {['scheduled', 'in_progress'].includes(j.status) && <Button size="sm" onClick={() => open('complete', j)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`job-complete-${j.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />{el ? 'Ολοκλήρωση' : 'Complete'}</Button>}
            <Button size="sm" variant="ghost" onClick={() => navigate(chatLink(j))} data-testid={`job-chat-${j.id}`}><MessageSquare className="w-4 h-4 mr-1" />{el ? 'Chat με υπεύθυνο' : 'Chat with manager'}</Button>
        </div>
    );
};

const JobCard = ({ j, el, open, quick, compact }) => (
    <div className={`fleet-card p-4 flex flex-col ${compact ? '' : 'md:flex-row md:items-center'} gap-4`} data-testid={`job-card-${j.id}`}>
        <div className="md:w-40 shrink-0">
            <p className="text-xs uppercase tracking-wide text-slate-400">{el ? 'Ραντεβού' : 'Appointment'}</p>
            <p className="text-lg font-bold text-slate-800" data-testid={`job-date-${j.id}`}>{fmtDay(j.scheduled_date, el)}</p>
            {j.status === 'date_proposed' && <p className="text-xs text-fuchsia-700">{el ? 'Πρόταση' : 'Proposed'}: {fmtDay(j.proposed_date, el)}</p>}
        </div>
        <div className="flex-1 min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-slate-800">{j.license_plate}</p>
                <span className="text-sm text-slate-500">{j.vehicle}</span>
                <MaintStatusBadge status={j.status} el={el} testId={`job-status-${j.id}`} />
            </div>
            <p className="text-sm text-slate-700">{j.service_type}{j.description ? ` · ${j.description}` : ''}</p>
            <p className="text-xs text-slate-500">{j.leasing_company_name ? `Leasing: ${j.leasing_company_name} · ` : ''}{el ? 'Μισθωτής' : 'Lessee'}: {j.client_company_name}</p>
            {j.return_comment && j.status === 'in_progress' && <p className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-sm px-2 py-1" data-testid={`job-return-comment-${j.id}`}>{el ? 'Επιστράφηκε από τον υπεύθυνο' : 'Returned by manager'}: {j.return_comment}</p>}
            {['awaiting_approval', 'completed'].includes(j.status) && (
                <p className="text-xs text-slate-500">✓ {fmtDay(j.completed_date, el)}{j.cost != null ? ` · €${j.cost}` : ''}{j.mileage != null ? ` · ${j.mileage} km` : ''}</p>
            )}
            {j.notes && <p className="text-xs text-slate-500">{el ? 'Σημειώσεις' : 'Notes'}: {j.notes}</p>}
        </div>
        <JobActions j={j} el={el} open={open} quick={quick} />
    </div>
);

export default function WorkshopJobsPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [jobs, setJobs] = useState(null);
    const [filter, setFilter] = useState('active');
    const [action, setAction] = useState(null);
    const [view, setView] = useState('list');
    const [selected, setSelected] = useState(null);
    const [selectedAppt, setSelectedAppt] = useState(null);
    const [isEdition, setIsEdition] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [week, setWeek] = useState(null);
    const [availability, setAvailability] = useState({});
    const config = dialogs(el);

    const load = useCallback(() => api.get('/leasing/workshop-jobs').then((r) => setJobs(r.data)).catch(() => setJobs([])), []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { api.get('/auth/company').then((r) => setIsEdition(r.data.edition === 'workshop')).catch(() => {}); }, []);
    const weekRef = useRef(null);
    const loadWeek = useCallback(() => {
        if (!week) return;
        weekRef.current = week;
        api.get('/workshop/availability', { params: { start: week, days: 7 } })
            .then((r) => { if (weekRef.current === week) setAvailability(Object.fromEntries(r.data.map((d) => [d.date, d]))); }).catch(() => {});
        if (isEdition) api.get('/workshop/appointments').then((r) => setAppointments(r.data)).catch(() => setAppointments([]));
    }, [week, isEdition]);
    useEffect(() => { loadWeek(); }, [loadWeek, jobs]);

    const openAction = (type, item) => { setSelected(null); setAction({ type, item }); };
    const quick = (j, path) => api.post(`/leasing/workshop-jobs/${j.id}/${path}`)
        .then(() => { toast.success(el ? 'Αποθηκεύτηκε' : 'Saved'); setSelected(null); load(); })
        .catch((e) => toast.error(e.response?.data?.detail || 'Error'));

    const match = (j, f) => (f === 'active' ? ACTIVE.includes(j.status) : j.status === f);
    const shown = (jobs || []).filter((j) => match(j, filter));
    const current = action && config[action.type];

    return (
        <div className="space-y-6" data-testid="workshop-jobs-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{el ? 'Εργασίες συνεργείου' : 'Workshop jobs'}</h1>
                    <p className="text-zinc-500">{el ? 'Ραντεβού συντήρησης που έχει προγραμματίσει ο υπεύθυνος στόλου' : 'Maintenance appointments scheduled by the fleet manager'}</p>
                </div>
                <div className="flex rounded-sm border border-slate-200 overflow-hidden" data-testid="job-view-toggle">
                    {[['list', List, el ? 'Λίστα' : 'List'], ['calendar', CalendarDays, el ? 'Ημερολόγιο' : 'Calendar']].map(([v, Icon, label]) => (
                        <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${view === v ? 'bg-[#1B7EB5] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`job-view-${v}`}>
                            <Icon className="w-4 h-4" />{label}
                        </button>
                    ))}
                </div>
            </div>
            {view === 'calendar' && jobs && <WorkshopWeekCalendar jobs={jobs} appointments={appointments} availability={availability} el={el} onSelect={setSelected} onSelectAppointment={setSelectedAppt} onWeekChange={setWeek} />}
            {view === 'list' && <div className="flex flex-wrap gap-2" data-testid="job-filters">
                {FILTERS.map((f) => (
                    <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-sm text-sm transition-colors ${filter === f ? 'bg-[#1B7EB5] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`job-filter-${f}`}>
                        {f === 'active' ? (el ? 'Ενεργές' : 'Active') : (el ? MAINT_STATUS[f].el : MAINT_STATUS[f].en)} ({(jobs || []).filter((j) => match(j, f)).length})
                    </button>
                ))}
            </div>}
            {view === 'list' && (jobs === null ? <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" /> : (
                <div className="space-y-3" data-testid="job-list">
                    {shown.map((j) => <JobCard key={j.id} j={j} el={el} open={openAction} quick={quick} />)}
                    {!shown.length && <div className="fleet-card p-8 text-center text-slate-400" data-testid="job-empty"><Wrench className="w-6 h-6 mx-auto mb-2" />{el ? 'Δεν υπάρχουν εργασίες' : 'No jobs'}</div>}
                </div>
            ))}
            {jobs === null && view === 'calendar' && <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" />}
            <Dialog open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="bg-white max-w-3xl" data-testid="cal-job-dialog">
                    <DialogHeader><DialogTitle>{el ? 'Ραντεβού' : 'Appointment'}</DialogTitle></DialogHeader>
                    {selected && <JobCard j={selected} el={el} open={openAction} quick={quick} compact />}
                </DialogContent>
            </Dialog>
            <Dialog open={Boolean(selectedAppt)} onOpenChange={(o) => !o && setSelectedAppt(null)}>
                <DialogContent className="bg-white max-w-2xl" data-testid="cal-appt-dialog">
                    <DialogHeader><DialogTitle>{el ? 'Ραντεβού πελάτη' : 'Customer appointment'}</DialogTitle></DialogHeader>
                    {selectedAppt && <AppointmentCard a={selectedAppt} el={el} onChanged={() => { setSelectedAppt(null); loadWeek(); }} />}
                </DialogContent>
            </Dialog>
            {current && (
                <MaintenanceFormDialog
                    open={Boolean(action)} onOpenChange={(o) => !o && setAction(null)} el={el} vehicles={[]}
                    title={`${current.title} - ${action.item.license_plate}`} fields={current.fields} submitLabel={current.submit}
                    endpoint={current.endpoint(action.item.id)} buildBody={current.body} onDone={load}
                />
            )}
        </div>
    );
}
